import React, { useState } from 'react'
import { ButtonRounded, Notification } from 'is24-corecss'
import { useTranslation } from 'react-i18next'
import { FeatureSwitch } from '../../../../utils/features/featureSwitches'
import { useFeatureSwitch } from '../../../../utils/features/FeatureSwitchContext'
import { IconS24Lightbulb24, IconS24Upload48 } from '@is24/cosma-ui-icons'
import { useAppSelector } from '../../../../app/hooks'
import { InitState } from '../../../../redux/reducers/root/initialState'
import { isEditEntryMode } from '../../../../@types/types'
import { AiDataExtractionUploadModal } from './AiDataExtractionUploadModal'
import { useToastContext } from '../../../../app/toast/toastContext'
import { trackAiDataExtractionBannerClick } from './aiDataExtractionTracking'
import styles from './AiDataExtractionBanner.module.css'

enum BannerMode {
  UploadPrompt = 'uploadPrompt',
  SuccessNotification = 'successNotification',
}

export const AiDataExtractionBanner = () => {
  const { t } = useTranslation()
  const toastContext = useToastContext()
  const { isActive: isAiDataExtractionReentryEnabled } = useFeatureSwitch(
    FeatureSwitch.PPA_12731_AI_DATA_EXTRACTION_REENTRY
  )
  const entryMode = useAppSelector(
    (state: InitState) => state.insertionFeature.insertionIdentification.metaInformation?.entryMode
  )
  const isAiDataExtractionSupported = useAppSelector(
    (state: InitState) => state.flowFeature.flowInformation.options?.hasAiDataExtractionBanner
  )
  const flowInformation = useAppSelector((state: InitState) => state.flowFeature.flowInformation)
  const isEditFlow = isEditEntryMode(entryMode)
  const [dismissedInSession, setDismissedInSession] = useState(false)
  const [bannerMode, setBannerMode] = useState(BannerMode.UploadPrompt)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false)
  const [extractionApiError, setExtractionApiError] = useState(false)

  if (dismissedInSession || isEditFlow || !isAiDataExtractionSupported) {
    return null
  }

  return (
    <div className='margin-bottom-l' data-testid='ai-data-extraction-banner'>
      {isAiDataExtractionReentryEnabled ? (
        <div className={styles.notificationWrapper}>
          <span className={styles.notificationBulb} data-testid='ai-data-extraction-banner-bulb'>
            <IconS24Lightbulb24 />
          </span>
          <Notification
            className={styles.notification}
            title={t('wizard.uploadAiBanner.v2.title')}
            message={t('wizard.uploadAiBanner.v2.message')}
            type={'neutral'}
            variant={'flat'}
            onClose={() => setDismissedInSession(true)}
            actionButton={
              <ButtonRounded
                variant={'filledWeak'}
                endElement={<IconS24Upload48 />}
                onClick={() => {
                  trackAiDataExtractionBannerClick(flowInformation, entryMode)
                  setIsUploadModalOpen(true)
                }}
              >
                {t('wizard.uploadAiBanner.cta')}
              </ButtonRounded>
            }
          />
        </div>
      ) : bannerMode === BannerMode.UploadPrompt ? (
        <Notification
          title={t('wizard.uploadAiBanner.title')}
          message={t('wizard.uploadAiBanner.message')}
          type={'neutral'}
          variant={'flat'}
          onClose={() => setDismissedInSession(true)}
          actionButton={
            <ButtonRounded
              variant={'filledWeak'}
              endElement={<IconS24Upload48 />}
              onClick={() => {
                trackAiDataExtractionBannerClick(flowInformation, entryMode)
                setIsUploadModalOpen(true)
              }}
            >
              {t('wizard.uploadAiBanner.cta')}
            </ButtonRounded>
          }
        />
      ) : (
        <div className='grid'>
          <div className='grid-item one-whole desk-seven-twelfths'>
            <Notification
              title=''
              message={t('wizard.uploadAiBanner.successNotification')}
              type={'info'}
              variant={'flat'}
              size={'medium'}
              data-testid='ai-data-extraction-success-notification'
            />
          </div>
        </div>
      )}
      <AiDataExtractionUploadModal
        extractionApiError={extractionApiError}
        isOpen={isUploadModalOpen}
        onClearExtractionApiError={() => setExtractionApiError(false)}
        onClose={() => {
          setIsUploadModalOpen(false)
          setExtractionApiError(false)
        }}
        onExtractionFailed={() => {
          setIsUploadModalOpen(true)
          setExtractionApiError(true)
        }}
        onSuccess={() => {
          setExtractionApiError(false)
          if (isAiDataExtractionReentryEnabled) {
            toastContext?.addToast({
              headline: t('wizard.uploadAiBanner.toast.headline'),
              message: t('wizard.uploadAiBanner.toast.message'),
              type: 'success',
              timeout: 6000,
              variant: 'elevated',
            })
            return
          }

          setBannerMode(BannerMode.SuccessNotification)
        }}
      />
    </div>
  )
}
